import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface FleetModeQuota {
  isFleetMode: boolean;
  totalAllowed: number;
  totalUsed: number;
  isLoading: boolean;
}

export const useFleetModeQuota = (deviceOrganizationId: string | null | undefined): FleetModeQuota => {
  // Query 1: Check if this organization has fleet mode enabled
  const { data: organization, isLoading: isLoadingOrg } = useQuery({
    queryKey: ['organization-fleet-mode', deviceOrganizationId],
    queryFn: async () => {
      if (!deviceOrganizationId) return null;
      const { data, error } = await supabase
        .from('organizations')
        .select('enable_fleet_mode')
        .eq('id', deviceOrganizationId)
        .single();
      
      if (error) {
        console.error('Error fetching organization fleet mode:', error);
        return null;
      }
      return data;
    },
    enabled: !!deviceOrganizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes - fleet mode setting changes rarely
  });

  const isFleetMode = organization?.enable_fleet_mode === true;

  // Query 2: If fleet mode enabled, aggregate all device quotas for this organization
  const { data: aggregatedQuota, isLoading: isLoadingQuota } = useQuery({
    queryKey: ['fleet-quota', deviceOrganizationId],
    queryFn: async () => {
      if (!deviceOrganizationId) return { totalAllowed: 0, totalUsed: 0 };

      // Get all users in this organization
      const { data: userRoles, error: userRolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('organization_id', deviceOrganizationId);

      if (userRolesError || !userRoles?.length) {
        console.error('Error fetching user roles for fleet mode:', userRolesError);
        return { totalAllowed: 0, totalUsed: 0 };
      }

      const userIds = userRoles.map(ur => ur.user_id).filter(Boolean);

      // Get all device assignments for these users
      const { data: assignments, error: assignmentsError } = await supabase
        .from('device_assignments')
        .select('device_id')
        .in('user_id', userIds);

      if (assignmentsError || !assignments?.length) {
        return { totalAllowed: 0, totalUsed: 0 };
      }

      const deviceIds = assignments.map(a => a.device_id);

      // Get quota data for all these devices
      const { data: deviceDetails, error: detailsError } = await supabase
        .from('device_details')
        .select('images_allowed, images_current')
        .in('device_id', deviceIds);

      if (detailsError || !deviceDetails?.length) {
        return { totalAllowed: 0, totalUsed: 0 };
      }

      // Aggregate the totals
      const totalAllowed = deviceDetails.reduce((sum, d) => sum + (d.images_allowed || 0), 0);
      const totalUsed = deviceDetails.reduce((sum, d) => sum + (d.images_current || 0), 0);

      return { totalAllowed, totalUsed };
    },
    enabled: isFleetMode && !!deviceOrganizationId,
    staleTime: 60 * 1000, // 1 minute - quota changes more frequently
  });

  return {
    isFleetMode,
    totalAllowed: aggregatedQuota?.totalAllowed ?? 0,
    totalUsed: aggregatedQuota?.totalUsed ?? 0,
    isLoading: isLoadingOrg || (isFleetMode && isLoadingQuota),
  };
};
